import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    CircularProgress,
    Alert,
    Typography,
    Chip
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { StaffService } from '../api/services/staffService';

export default function ApproveLeaveModal({ open, onClose, leaveRequest, action, onSuccess }) {
    const [comments, setComments] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isApprove = action === 'Approved';

    useEffect(() => {
        if (open) {
            setComments('');
            setError('');
        }
    }, [open]);

    const handleSubmit = async () => {
        // Comments are required when rejecting
        if (!isApprove && !comments.trim()) {
            setError('A reason is required when rejecting a leave request.');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            const payload = {
                status: action,
                ...(comments.trim() && { comments: comments.trim() })
            };
            await StaffService.approveLeaveRequest(leaveRequest.id || leaveRequest._id, payload);
            onSuccess(
                `Leave request ${isApprove ? 'approved' : 'rejected'} successfully`,
                leaveRequest.id || leaveRequest._id,
                action
            );
            onClose();
        } catch (err) {
            const msg = err.response?.data?.message || err.message || 'Failed to update leave request.';
            setError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!leaveRequest) return null;

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isApprove
                    ? <><CheckCircleOutlineIcon color="success" /> Approve Leave Request</>
                    : <><CancelOutlinedIcon color="error" /> Reject Leave Request</>
                }
            </DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                {/* Leave summary */}
                <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Staff Member</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
                        {leaveRequest.staff?.fullName || leaveRequest.staffName || leaveRequest.staff || 'Unknown'}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3 }}>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Leave Type</Typography>
                            <Typography variant="body2">{leaveRequest.leaveType || leaveRequest.type || '--'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">From</Typography>
                            <Typography variant="body2">{leaveRequest.startDate || '--'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">To</Typography>
                            <Typography variant="body2">{leaveRequest.endDate || '--'}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                            <Chip label={leaveRequest.status} size="small" color="warning" />
                        </Box>
                    </Box>
                    {leaveRequest.reason && (
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" color="text.secondary">Reason</Typography>
                            <Typography variant="body2">{leaveRequest.reason}</Typography>
                        </Box>
                    )}
                </Box>

                <TextField
                    label={isApprove ? 'Comments (Optional)' : 'Reason for Rejection *'}
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder={isApprove ? 'e.g. Enjoy your holiday!' : 'Please provide a reason for rejection...'}
                    error={!!error && !isApprove && !comments.trim()}
                    disabled={isSubmitting}
                    required={!isApprove}
                />
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button
                    variant="contained"
                    color={isApprove ? 'success' : 'error'}
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting
                        ? <CircularProgress size={20} color="inherit" />
                        : isApprove ? <CheckCircleOutlineIcon /> : <CancelOutlinedIcon />
                    }
                >
                    {isSubmitting
                        ? (isApprove ? 'Approving...' : 'Rejecting...')
                        : (isApprove ? 'Approve' : 'Reject')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
